/**
 * Pantalla de entrada.
 *
 * Pide el correo y manda un enlace. Después de enviarlo dice siempre lo mismo,
 * exista o no ese correo: si el mensaje cambiara, cualquiera podría averiguar
 * quién trabaja aquí probando direcciones (ADR-003).
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { requestMagicLink, ApiError } from "@web/lib/api-client.ts";
import { Button, Field, Notice } from "@web/components/primitives.tsx";
import { GuardiaLogo } from "@web/components/brand.tsx";

/** Motivos con que vuelve el callback cuando el enlace no sirvió. */
const ENTRY_MESSAGES: Record<string, string> = {
  vencida: "Ese enlace ya venció. Pide uno nuevo: valen 15 minutos.",
  usada: "Ese enlace ya se usó. Los enlaces sirven una sola vez.",
  invalida: "Ese enlace no es válido. Pide uno nuevo.",
  "sin-configurar": "La entrada no está configurada. Avísale a soporte.",
};

export function Login() {
  const [email, setEmail] = useState("");

  // El callback redirige con ?entrada=<motivo> cuando algo falló.
  const entryProblem = new URLSearchParams(window.location.search).get("entrada");

  const send = useMutation({
    mutationFn: () => requestMagicLink(email.trim()),
  });

  return (
    <div className="flex min-h-dvh flex-col bg-gd-deep">
      {/* El azul oscuro es el fondo de entrada; la tarjeta blanca es la única
          superficie clara, para que la mirada caiga donde hay que escribir. */}
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-5 py-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <GuardiaLogo className="h-11" />
          <p className="mt-4 font-display text-[22px] font-bold tracking-tight text-white">
            GuardIA Driving
          </p>
          <p className="mt-1 text-[15px] text-white/60">Análisis de conducción sobre la flota</p>
        </div>

        <div className="gd-card p-6 sm:p-7">
          {entryProblem && ENTRY_MESSAGES[entryProblem] ? (
            <div className="mb-5">
              <Notice tone="warning" title="No se pudo entrar">
                {ENTRY_MESSAGES[entryProblem]}
              </Notice>
            </div>
          ) : null}

          {send.isSuccess ? (
            <Notice title="Revisa tu correo">
              Si <span className="font-semibold">{email.trim()}</span> está registrado, te llegó un
              enlace. Vence en 15 minutos y sirve una sola vez.
            </Notice>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (email.trim()) send.mutate();
              }}
            >
              <Field
                label="Correo"
                htmlFor="correo"
                error={
                  send.isError
                    ? send.error instanceof ApiError
                      ? send.error.message
                      : "No se pudo enviar el enlace."
                    : undefined
                }
              >
                <input
                  id="correo"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  aria-describedby={send.isError ? "correo-error" : undefined}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="nombre@grupoarcos.com"
                />
              </Field>

              <Button type="submit" className="w-full" disabled={send.isPending || !email.trim()}>
                {send.isPending ? "Enviando…" : "Enviarme el enlace"}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-[13px] leading-relaxed text-white/45">
          No hay contraseña que recordar ni que perder. Cada entrada usa un enlace nuevo que caduca.
        </p>
      </main>
    </div>
  );
}
